<script lang="ts" setup>
import { Heatpump } from '@/services/models/implementations/Heatpump.ts'
import i18n, { formatNumber } from '@/helper/Translations.ts'
import { CharacteristicType, DownloadExcelType, HeatPumpStat, HpBadge, InstallationType } from '@/helper/Types.ts'
import userStore from '@/store/UserStore.ts'
import projectStore from '@/store/ProjectStore.ts'
import { onMounted, ref } from 'vue'
import { api } from '@/services/ApiService.ts'

const props = defineProps<{
  heatPump: Heatpump
}>()

const container = ref<HTMLElement | null>(null)
const statElements = ref<HTMLElement[] | null>(null)
const isOpen = ref<boolean>(false)
const singleRowHeight = ref<number>(0)
const height = ref<string>()

const stats = getHpBadgeData()

const maxColumnCount = 5
const containerPadding = 8
const isExpandable = stats.length > maxColumnCount

onMounted(() => {
  singleRowHeight.value = Math.max(...statElements.value!.map(el => el.offsetHeight))
  height.value = `${singleRowHeight.value}px`
})

function clickOpen(): void {
  if (!isExpandable) {
    return
  }

  if (isOpen.value) {
    height.value = `${singleRowHeight.value}px`
  } else {
    height.value = `${Math.ceil(stats.length / maxColumnCount) * singleRowHeight.value + containerPadding}px`
  }
  isOpen.value = !isOpen.value
}

function getHpBadgeData(): HpBadge[] {
  let data: HpBadge[] = []
  let pump = props.heatPump

  if (projectStore.isHeating) {
    const heatingPerformance: HpBadge = {
      key: HeatPumpStat.HEATING_PERFORMANCE,
      value: `${formatNumber(projectStore.getHeatingPowerOutput(pump.chr))} kW`,
      description: i18n.global
        .t(`components.heating_performance.${projectStore.installationTypeName(InstallationType.HEATING)}`)
        .toString()
    }

    if (userStore.isPrivileged) {
      heatingPerformance.clickHandler = () => {
        api.downloadHeatPumpExcel(
          projectStore.projectId,
          DownloadExcelType.HEATPUMP_PERFORMANCE,
          'heating-performance',
          props.heatPump.id,
          InstallationType.HEATING,
          props.heatPump.chr.cascadeSize
        )
      }
    }

    data.push(heatingPerformance)

    data.push({
      key: HeatPumpStat.PEAK_HEAT_LOSS_COVERAGE,
      value: `${Math.round(pump.chr?.heating?.loadCoveragePercent ?? 0)} %`,
      description: i18n.global
        .t(`components.heat_loss_coverage.${projectStore.installationTypeName(InstallationType.HEATING)}`)
        .toString()
    })
  }

  if (projectStore.isCooling) {
    const coolingPerformance: HpBadge = {
      key: HeatPumpStat.COOLING_PERFORMANCE,
      value: `${formatNumber(pump.chr?.cooling?.operatingPoint?.powerOutput)} kW`,
      description: i18n.global
        .t(`components.heating_performance.${projectStore.installationTypeName(InstallationType.COOLING)}`)
        .toString()
    }

    data.push({
      key: HeatPumpStat.COOLING_LOAD_COVERAGE,
      value: `${Math.round(pump.chr?.cooling?.loadCoveragePercent ?? 0)} %`,
      description: i18n.global
        .t(`components.heat_loss_coverage.${projectStore.installationTypeName(InstallationType.COOLING)}`)
        .toString() // Cooling Load Coverage
    })

    if (userStore.isPrivileged) {
      coolingPerformance.clickHandler = () => {
        api.downloadHeatPumpExcel(
          projectStore.projectId,
          DownloadExcelType.HEATPUMP_PERFORMANCE,
          'cooling-performance',
          props.heatPump.id,
          InstallationType.COOLING,
          props.heatPump.chr.cascadeSize
        )
      }
    }

    data.push(coolingPerformance)
  }

  const integratedDhwCylinder = pump.getProductCharacteristic(CharacteristicType.DHW_STORAGE)
  if (integratedDhwCylinder) {
    data.push({
      key: HeatPumpStat.DHW_VOLUME,
      value: `${formatNumber(integratedDhwCylinder.volume)} l`,
      description: i18n.global.t('components.dhw_volume').toString()
    })
  }

  if (projectStore.isHeating) {
    const bivalencePoint: HpBadge = {
      key: HeatPumpStat.BIVALENCE_POINT,
      value: `${Math.round(pump.chr?.heating?.operatingPointAtBivalencePoint?.sourceTemperature ?? 0)} °C`,
      description: i18n.global.t('components.bivalence_point').toString()
    }

    if (userStore.isPrivileged) {
      bivalencePoint.clickHandler = () => {
        api.downloadHeatPumpExcel(
          projectStore.projectId,
          DownloadExcelType.BIVALENCE_POINT,
          'bivalence-point',
          props.heatPump.id,
          InstallationType.HEATING,
          props.heatPump.chr.cascadeSize
        )
      }
    }
    data.push(bivalencePoint)
  }

  if (pump.chr?.noiseValues && pump.chr?.noiseValues?.distanceAtNoiseLevel) {
    data.push({
      key: HeatPumpStat.DISTANCE_DBA,
      value: `${pump.chr?.noiseValues?.distanceAtNoiseLevel} m`,
      description: i18n.global.t('components.distance_dba', { num: userStore.nationalNoiseLimitation }).toString()
    })
  }

  if (userStore.showOperatingPoints) {
    if (projectStore.isHeating) {
      const operatingPoint = pump.chr?.heating?.operatingPoint

      if (operatingPoint
        && operatingPoint.flowTemperature !== projectStore.heatingFlowTemperature
        && operatingPoint.flowTemperature !== userStore.heatPumpSuggestionHybridFlowTemperature) {

        operatingPoint.label = '-'
      }

      data.push({
        key: HeatPumpStat.HEATING_OPERATION_POINT,
        value: `${operatingPoint?.label}`,
        description: i18n.global.t('components.heating_operation_point')
      })
    }

    if (projectStore.isCooling) {
      data.push({
        key: HeatPumpStat.COOLING_OPERATION_POINT,
        value: `${pump.chr?.cooling?.operatingPoint?.label}`,
        description: i18n.global.t('components.cooling_operation_point')
      })
    }
  }

  if (projectStore.projectProperties.groundType.val && pump.chr?.boreholes) {
    const formattedDepth =
      pump.chr?.boreholes?.numberOfBoreholes > 1
        ? `${pump.chr?.boreholes?.numberOfBoreholes}x ${Math.ceil(pump.chr.boreholes.drillingDepth)} m`
        : `${Math.ceil(pump.chr.boreholes.drillingDepth)} m`

    data.push({
      key: HeatPumpStat.DRILLING_DEPTH,
      value: formattedDepth,
      description: i18n.global.t('components.drilling_depth')
    })
  }

  if (userStore.heatPumpStatsConfig.length) {
    let order: { key: HeatPumpStat; order: number }[] = []
    order = userStore.heatPumpStatsConfig.map((stat, i) => ({ key: stat, order: i }))
    data = data.filter(stat => userStore.heatPumpStatsConfig.includes(stat.key))
    data.sort((a, b) => (order.find(c => c.key === a.key)?.order ?? 0) - (order.find(c => c.key === b.key)?.order ?? 0))
  }

  return data
}
</script>

<template>
  <div
    ref="container" :class="{ expandable: isExpandable }" :style="{
      height: height,
      cursor: isExpandable ? 'pointer' : 'initial',
      gridTemplateColumns: `repeat(${stats.length <= maxColumnCount ? stats.length : maxColumnCount}, 1fr)`,
    }"
    class="component__data"
    @click.prevent="clickOpen"
  >
    <div
      v-for="(data, i) in stats" :key="`data-${props.heatPump.id}-${i}`" ref="statElements" :class="{
        invisible: i >= maxColumnCount && !isOpen,
        clickable: !!data.clickHandler
      }"
      :style="{
        height: `${singleRowHeight}px`,
      }"
      class="data"
      @click="data.clickHandler"
    >
      <span class="data__value h3">{{ data.value }}</span>
      <span class="data__description small-text">{{ data.description }}</span>
    </div>
  </div>

  <a v-if="isExpandable" :class="{ open: isOpen}" class="expand" @click.prevent.stop="clickOpen">
    {{ $t('step_heat_pumps.show_more_stats') }} <i class="ic-chevron-down" />
  </a>
</template>

<style lang="scss" scoped>
@import '@/assets/style/helpers/include-media';
@import '@/assets/style/helpers/mixins';

.expand {
  position: relative;
  z-index: 1;
  text-decoration: none;
  display: flex;
  justify-content: center;
  gap: rem-calc(2);
  margin-top: rem-calc(8);

  &:hover {
    cursor: pointer;
  }

  i {
    font-size: rem-calc(18);
  }

  &.open {
    i {
      transform: rotate(180deg);
    }
  }

  @include media('<desktop') {
    display: none;
  }
}

.component__data {
  box-sizing: content-box;
  overflow: hidden;
  transition: height .25s ease-in-out;
  position: relative;
  z-index: 1;
  border-color: $borderColor;
  padding: rem-calc(8);
  gap: rem-calc(8);
  display: inline-grid;
  grid-auto-rows: 1fr;

  @include media('<desktop') {
    padding: 0;
    gap: 0;
    display: flex;
    flex-direction: column;
    height: auto !important;
  }

  @include themify($themes) {
    border-radius: rem-calc(themed('cardBorderRadius'));
  }

  @include media('>=desktop') {
    &.expandable {
      .data {
        min-height: rem-calc(73);
      }
    }
  }
}

.data {
  display: flex;
  align-items: center;
  transition: all .3s;
  border-bottom: 1px solid $borderColorLight;
  text-align: center;
  flex-direction: row-reverse;
  width: 100%;
  justify-content: space-between;
  height: auto !important;
  padding: 0 rem-calc(12);

  &.clickable:hover {
    cursor: pointer;

    .data__description {
      text-decoration: underline;
    }
  }

  &__value {
    line-height: 1;
    margin-bottom: 0;
    align-items: flex-start;
    padding-top: rem-calc(4);

    &.h3 {
      font-size: rem-calc(19);
    }

    @include themify($themes) {
      color: themed('primaryColor');
      margin-bottom: rem-calc(4);
    }
  }

  &__description {
    font-size: rem-calc($textSize);
  }

  @include media('>=desktop') {
    flex-direction: column;
    width: auto;
    border-bottom: none;
    padding: rem-calc(8);
    justify-content: flex-start;

    &__value {
      padding-top: 0;
    }

    &__description {
      font-size: rem-calc($textSmallSize);
    }

    &.invisible {
      opacity: 0;
      visibility: hidden;
    }

    &.small-text {
      min-height: rem-calc(28);
      display: flex;
      align-items: center;
    }
  }
}
</style>
